import { getSessionContext, getSessionToken, getWebSdk } from "@/lib/pw-auth";
import { HttpError } from "@/lib/api/http-error";

export type ApiRequestInit = Omit<RequestInit, "body"> & { body?: unknown };

const SDK_VERSION = "0.0.27-alpha-1";

/** Mirrors the PW SDK's own request headers; it rejects authorised calls that omit `randomId`. */
async function authHeaders(): Promise<Record<string, string>> {
  if (typeof document === "undefined") {
    return {};
  }

  const session = getSessionContext();

  // Anonymous visitors (pricing, starting price) must not pull the auth SDK into the first paint.
  if (!session) {
    return {};
  }

  const webSdk = await getWebSdk();
  const organizationId = process.env.NEXT_PUBLIC_PW_ORG_ID ?? "";

  return {
    // The SDK rotates the token in place, so prefer it over the cookie we bootstrapped from.
    Authorization: `Bearer ${webSdk?.accessToken || session.token}`,
    randomId: webSdk?.randomId ?? session.randomId ?? "",
    "client-id": organizationId,
    organizationid: organizationId,
    "client-type": "WEB",
    "X-SDK-Version": SDK_VERSION,
  };
}

let refreshPromise: Promise<string | undefined> | undefined;

/** Token refresh lives in the SDK's own client, so drive it by hand for our plain-`fetch` calls. */
function refreshAuthToken() {
  refreshPromise ??= (async () => {
    const webSdk = await getWebSdk();

    try {
      await webSdk?.refreshToken();
    } catch (error) {
      console.error("[api] Unable to refresh the PW session token", error);
      return undefined;
    }

    return webSdk?.accessToken || getSessionToken();
  })().finally(() => {
    refreshPromise = undefined;
  });

  return refreshPromise;
}

/** Response interceptor: parses JSON and turns non-2xx responses into a thrown `HttpError`. */
async function parseResponse<T>(response: Response): Promise<T> {
  const isJson = response.headers.get("content-type")?.includes("application/json");
  const body = isJson ? await response.json().catch(() => undefined) : undefined;

  if (!response.ok) {
    throw new HttpError(response.status, response.statusText, body);
  }

  return body as T;
}

/**
 * Base fetcher for calls to the PW API. Adds the base URL, JSON body/headers and the auth
 * interceptor; throws `HttpError` on failure. Use the `useFetch`/`useMutation` hooks from
 * components instead of calling this directly.
 */
export async function apiFetch<T>(
  path: string,
  { body, headers, ...init }: ApiRequestInit = {},
): Promise<T> {
  const baseUrl = process.env.NEXT_PUBLIC_PW_API_BASE_URL;

  if (!baseUrl) {
    throw new Error("NEXT_PUBLIC_PW_API_BASE_URL is not set.");
  }

  const url = `${baseUrl.replace(/\/$/, "")}${path}`;
  const baseHeaders: Record<string, string> = {
    ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
    ...(headers as Record<string, string> | undefined),
  };
  const serialisedBody = body !== undefined ? JSON.stringify(body) : undefined;

  const send = async () =>
    fetch(url, {
      ...init,
      headers: { ...(await authHeaders()), ...baseHeaders },
      body: serialisedBody,
    });

  let response = await send();

  if (
    response.status === 401 &&
    typeof document !== "undefined" &&
    (await refreshAuthToken())
  ) {
    response = await send();
  }

  return parseResponse<T>(response);
}
